import CloseIcon from "@mui/icons-material/Close";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Snackbar
} from "@mui/material";
import Alert from "@mui/material/Alert";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { WAREHOUSE_PLAN_ID } from "components/WarehouseElements/Grid/WarehouseGrid/WarehouseGrid";
import { ErrorData } from "models/Errors/ErrorResponse";
import React, { JSX } from "react";
import { createPortal } from "react-dom";
import ClipBoardTexte from "utils/copyClipboard";
import "./ErrorComponent.scss";

type ErrorComponentProps = {
  errorData: ErrorData;
  status: number;
};

export const ERROR_SNACKBAR_DURATION_MS = 10 * 1000;

export function ErrorComponent({ errorData, status }: ErrorComponentProps): JSX.Element {
  const [open, setOpen] = React.useState(status !== null);

  const handleClose = () => {
    setOpen(false);
  };

  if (status.toString()["0"] === "4") {
    return (
      <Dialog
        open={open}
        className="dialog-window"
        id="error-dialog"
        maxWidth={false}
        slotProps={{
          paper: {
            sx: {
              minWidth: "600px",
              minHeight: "250px",
            },
          },
        }}
        onClick={e => e.stopPropagation()}>
        <DialogTitle id="error-title">
          {"La requête à échouée"}
          <IconButton
            id="dialog-close-btn"
            aria-label="close"
            onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          dividers
          className="dialog-body error-body"
          style={{
            padding: "45px",
            borderStyle: "hidden",
          }}>
          <ErrorOutlineIcon
            className="error-icon"
            fontSize="large"
            sx={{ fontSize: 60 }}
          />
          <div style={ { maxHeight: "20em", overflow: "auto" }}>{errorData.detail.split("\r\n").map((line, idx) => <div key={"ln-"+idx}><ClipBoardTexte key={"cb-"+idx} texte={line} ></ClipBoardTexte></div>)} {` (${errorData.code})`}</div>
          <div className="error-request-id">{errorData.requestId}</div>
        </DialogContent>
        <DialogActions style={{ justifyContent: "center" }}>
          <Button
            autoFocus
            className="dialog-buttons"
            style={{
              backgroundColor: "#1976d2",
              color: "white",
              width: "35%",
            }}
            onClick={handleClose}>
            {"Ok"}
          </Button>
        </DialogActions>
      </Dialog>
    );
  } else {
    return createPortal(
      <Snackbar
        open={true}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        autoHideDuration={ERROR_SNACKBAR_DURATION_MS}
        className="error-component-snackbar">
        <Alert severity="error" variant="filled" className="error-alert">
          {`Problème interne, veuillez communiquer avec le département TI. (${errorData.requestId})`}
        </Alert>
      </Snackbar>,
      document.getElementById(WAREHOUSE_PLAN_ID)
        ? document.getElementById(WAREHOUSE_PLAN_ID)!
        : document.body
    );
  }
}

type ErrorSnackBarProps = {
  errorMessage: string;
};

export function ErrorSnackBar({ errorMessage }: ErrorSnackBarProps): JSX.Element {
  const [open, setOpen] = React.useState(true);

  const handleClose = () => {
    console.log("Closing error snackbar");
    setOpen(false);
  };

  return createPortal(
    <Snackbar
      open={open}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      onClose={handleClose}
      autoHideDuration={ERROR_SNACKBAR_DURATION_MS}>
      <Alert severity="error" variant="filled" className="error-component">
        {errorMessage}
      </Alert>
    </Snackbar>,
    document.getElementById(WAREHOUSE_PLAN_ID)
      ? document.getElementById(WAREHOUSE_PLAN_ID)!
      : document.body
  );
}

export function createErrorComponent(error?: FetchBaseQueryError) {
  if (error) {
    const errorStatus = error.status as number;
    let errorData: ErrorData;
    if (error.data) {
      errorData = error.data as ErrorData;
      return <ErrorComponent errorData={errorData} status={errorStatus} />;
    }
    return createErrorSnackBar(errorStatus + ": Erreur inattendue");
  }
  return createErrorSnackBar("Erreur inattendue");
}

export function createErrorSnackBar(errorMessage?: string) {
  if (errorMessage) {
    return <ErrorSnackBar errorMessage={errorMessage} />;
  }
  return <ErrorSnackBar errorMessage={"Erreur inattendue"} />;
}
